// Typed access to the `opts` config. Tiered-margin helper below MUST be used
// for every margin calculation per Tom's instruction — never read opts.profitMarginPct directly.

import { useAppState } from './useAppState'
import type { Opts } from '../types'

const DEFAULT_OPTS: Opts = {
  profitMarginEnabled: false,
  profitMarginPct: 0,
  dateFormat: 'MM/DD/YYYY',
}

export function useOpts(): Opts {
  const { data } = useAppState()
  return data?.opts ?? DEFAULT_OPTS
}

// Ported from vanilla getMarginPct (public/app/index.html L5137).
// Iterates the tiered thresholds in ascending upTo order; returns the first
// tier where subtotal <= upTo (or null/undefined upTo = catch-all).
// Falls back to the flat profitMarginPct when no tiers are configured.
export function useMarginPct(subtotal: number): number {
  const opts = useOpts()
  return getMarginPct(opts, subtotal)
}

export function getMarginPct(opts: Opts, subtotal: number): number {
  if (!opts.profitMarginEnabled) return 0
  const tiers = opts.profitMarginThresholds
  if (tiers && tiers.length) {
    const sorted = [...tiers].sort((a, b) => {
      if (a.upTo == null) return 1
      if (b.upTo == null) return -1
      return a.upTo - b.upTo
    })
    for (const t of sorted) {
      if (t.upTo == null || subtotal <= t.upTo) return t.pct || 0
    }
  }
  return opts.profitMarginPct ?? 0
}

// T&M draw fee% — project-level override beats opts margin tier.
// Ported from V1 getProjectFeePct (public/app/index.html:5146).
export function getProjectFeePct(
  project: { feePctOverride?: number | string | null } | undefined | null,
  opts: Opts,
  subtotal: number,
): number {
  const ov = project?.feePctOverride
  if (ov !== undefined && ov !== null && ov !== '') {
    const n = typeof ov === 'string' ? parseFloat(ov) : ov
    if (!isNaN(n)) return n
  }
  return getMarginPct(opts, subtotal)
}
